import { applyThemeTo } from "../lib/theme";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";

const SettingsContext = createContext(null);

const DEFAULT_FEATURE_FLAGS = {
  calculators: true, library: true, inventory: true, cycles: true,
  weight: true, nutrition: true, supplements: true, history: true, feed: true, tutorial: true,
  community: true, playbook: true,
};

const DEFAULTS = {
  site_name: "The Amino Exchange",
  tagline: "Precision Dose Protocol",
  primary_color: "#0D9488",
  logo_url: null,
  favicon_url: null,
  maintenance_enabled: false,
  maintenance_title: "Scheduled maintenance",
  maintenance_body: "We're rolling out an update. The Amino Exchange will be back online shortly — thanks for your patience.",
  maintenance_eta: "",
  feature_flags: DEFAULT_FEATURE_FLAGS,
  site_timezone: "America/Chicago",
};

const DEFAULT_APPLE_ICON = "/icon-192.png";
const DEFAULT_MANIFEST = "/manifest.json";

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const originalFaviconRef = useRef(null);
  const originalAppleIconRef = useRef(null);
  const originalManifestRef = useRef(null); // cached JSON of /manifest.json
  const previousBlobUrlRef = useRef(null);  // so we can revoke old blob urls

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      // Merge feature_flags with defaults so partial rows never leave a section missing.
      const flags = { ...DEFAULT_FEATURE_FLAGS, ...(data?.feature_flags || {}) };
      setSettings({ ...DEFAULTS, ...data, feature_flags: flags });
    } catch {
      // keep defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    // Poll every 45s so the maintenance flag propagates without a manual reload
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
  }, [load]);

  // Apply brand color as a CSS variable so component styles can reference var(--brand)
  useEffect(() => {
    if (settings?.primary_color) {
      document.documentElement.style.setProperty("--brand", settings.primary_color);
    }
    if (settings?.site_name) {
      document.title = settings.site_name;
    }
    // Apply Theme Studio tokens (falls back to defaults if unset)
    applyThemeTo(document.documentElement, settings?.theme);

    // ---------- Favicon ----------
    // Admin-managed favicon (any image mime — png/svg/ico). Falls back to whatever
    // shipped in public/index.html when unset.
    let iconLink = document.querySelector("link[rel~='icon']") || document.querySelector("link[rel='shortcut icon']");
    if (!iconLink) {
      iconLink = document.createElement("link");
      iconLink.setAttribute("rel", "icon");
      document.head.appendChild(iconLink);
    }
    if (originalFaviconRef.current === null) {
      originalFaviconRef.current = iconLink.getAttribute("href") || "/favicon.ico";
    }
    if (settings?.favicon_url) {
      iconLink.setAttribute("href", settings.favicon_url);
    } else if (originalFaviconRef.current) {
      iconLink.setAttribute("href", originalFaviconRef.current);
    }

    // ---------- Apple touch icon (iOS home-screen PWA) ----------
    let appleLink = document.querySelector("link[rel='apple-touch-icon']");
    if (!appleLink) {
      appleLink = document.createElement("link");
      appleLink.setAttribute("rel", "apple-touch-icon");
      document.head.appendChild(appleLink);
    }
    if (originalAppleIconRef.current === null) {
      originalAppleIconRef.current = appleLink.getAttribute("href") || DEFAULT_APPLE_ICON;
    }
    if (settings?.favicon_url) {
      appleLink.setAttribute("href", settings.favicon_url);
    } else {
      appleLink.setAttribute("href", originalAppleIconRef.current);
    }

    // ---------- PWA manifest (Android / desktop install prompt) ----------
    // We swap the shipped /manifest.json for a Blob URL whose icons point to the
    // admin-uploaded favicon. This keeps the PWA install screen on-brand.
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.setAttribute("rel", "manifest");
      document.head.appendChild(manifestLink);
      manifestLink.setAttribute("href", DEFAULT_MANIFEST);
    }
    const applyManifest = (base) => {
      if (!base) return;
      // Base copy so we don't mutate the cached original.
      const next = { ...base };
      if (settings?.site_name) {
        next.name = `${settings.site_name} — ${settings.tagline || DEFAULTS.tagline}`;
        next.short_name = settings.site_name;
      }
      if (settings?.primary_color) {
        // theme_color is what Android uses to tint the address bar / splash
        next.theme_color = settings.primary_color;
      }
      if (settings?.favicon_url) {
        next.icons = [
          { src: settings.favicon_url, sizes: "192x192", type: "image/png", purpose: "any maskable" },
          { src: settings.favicon_url, sizes: "512x512", type: "image/png", purpose: "any maskable" },
        ];
      } else if (base.icons) {
        next.icons = base.icons;
      }
      const blob = new Blob([JSON.stringify(next)], { type: "application/manifest+json" });
      const url = URL.createObjectURL(blob);
      if (previousBlobUrlRef.current) URL.revokeObjectURL(previousBlobUrlRef.current);
      previousBlobUrlRef.current = url;
      manifestLink.setAttribute("href", url);
    };
    if (!originalManifestRef.current) {
      fetch(DEFAULT_MANIFEST)
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((json) => {
          originalManifestRef.current = json;
          applyManifest(json);
        })
        .catch(() => {
          // Fall back to a minimal manifest so the PWA is still installable.
          const fallback = {
            short_name: settings?.site_name || DEFAULTS.site_name,
            name: settings?.site_name || DEFAULTS.site_name,
            start_url: "/dashboard",
            scope: "/",
            display: "standalone",
            theme_color: settings?.primary_color || DEFAULTS.primary_color,
            background_color: "#0a0a0b",
            icons: [],
          };
          originalManifestRef.current = fallback;
          applyManifest(fallback);
        });
    } else {
      applyManifest(originalManifestRef.current);
    }
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refreshSettings: load, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext) || { settings: DEFAULTS, loaded: false, refreshSettings: () => {} };
